import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/GlobalProvider";
import { GoPerson } from "react-icons/go";
import { IoBagHandleOutline } from "react-icons/io5";
import { IoIosLogOut } from "react-icons/io";
import { BsBag } from "react-icons/bs";
import { GoQuestion } from "react-icons/go";
import { FiMenu } from "react-icons/fi";
import { FaBusinessTime } from "react-icons/fa";
import axios from "axios";
import { SETTINGS_CRUD_API } from "../../hooks/APIHooks";

const Navbar = () => {
  const location = useLocation();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [cartItemCount, setCartItemCount] = useState(0);
  const dropdownRef = useRef(null);
  const [companyData, setCompanyData] = useState({ 
    companyName: "",
    companylogo: ""
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCompanySettings();
  }, []);

  // Function to get proper image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return "";
    
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http')) return imagePath;
    
    // If it's a relative path, construct full URL
    if (imagePath.startsWith('/')) {
      return `${window.location.origin}${imagePath}`;
    }
    
    // For other cases, try to construct URL
    const cleanPath = imagePath.replace(/^(\.?\/?)?/, '');
    
    // Handle different environments
    let baseUrl;
    const { hostname, port, protocol } = window.location;
    
    if (hostname === 'localhost' && port === '3000') {
      // React dev server on port 3000, APIs on port 80
      baseUrl = `${protocol}//${hostname}`;
    } else if (hostname === 'localhost' && !port) {
      // React build served from same domain as APIs
      baseUrl = window.location.origin;
    } else {
      // Production environment
      baseUrl = window.location.origin;
    }
    
    return `${baseUrl}/apis/${cleanPath}`;
  };

  // Handle image loading errors
  const handleImageError = (e) => {
    console.warn("Logo failed to load:", e.target.src);
    e.target.style.display = 'none';
  };

  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(SETTINGS_CRUD_API);
      if (response.data && !response.data.error) {
        setCompanyData(response.data);
      }
    } catch (err) {
      console.error("Error fetching company settings:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    signOut();
    navigate("/signin");
  };

  // Toggle dropdown visibility for profile
  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
  };

  useEffect(() => {
    const cart = JSON.parse(localStorage.getItem("cart")) || [];
    const totalItems = cart.reduce((total, item) => total + item.quantity, 0);
    setCartItemCount(totalItems);
  }, []);

  // Close dropdown when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  return (
    <div className="sticky w-full z-[99] top-0 bg-white shadow-md ">
      <div className="flex items-center justify-between p-2 border-b border-gray-200">
        {/* Left Section */}
        <div className="flex items-center space-x-4">
          <Link to="/Store">
            <FiMenu size={30} className="text-gray-700 hover:text-yellow-500" />
          </Link>
        </div>

        {/* Center Section - Logo and Company Name */}
        <div className="text-center flex-1">
          <Link to="/" className="flex items-center justify-center space-x-3">
            {/* Company Logo */}
            {companyData.companylogo && (
              <div className="flex-shrink-0">
                <img
                  src={getImageUrl(companyData.companylogo)}
                  alt={`${companyData.companyName} Logo`}
                  className="w-8 h-8 object-contain rounded"
                  onError={handleImageError}
                  loading="lazy"
                />
              </div>
            )}
            
            {/* Company Name */}
            <span className="text-[25px] font-bold text-black">
              {companyData.companyName || "Company Name"}
            </span>
          </Link>
        </div>

        {/* Right: User Profile, Cart, Help */}
        <div className="flex items-center space-x-2 text-black">
          <div
            className="relative cursor-pointer"
            onClick={toggleProfileDropdown}
          >
            <GoPerson size={25} />
            {isProfileDropdownOpen && (
              <div
                ref={dropdownRef}
                className="absolute right-0 bg-white border border-gray-300 shadow-lg rounded-lg w-60 p-4 z-50 max-h-[calc(100vh-80px)] overflow-auto"
              >
                <div className="text-center mb-3">
                  <p className="font-bold text-md">
                    Hello, {user?.name ? user.name : "Guest"}
                  </p>
                  <p className="text-xs text-gray-500">
                    {user
                      ? "Find all your details here"
                      : "To access your account"}
                  </p>
                </div>

                {user ? (
                  <div>
                    <Link
                      to="/user/dashboard"
                      className="flex items-center space-x-2 py-2 text-sm text-black hover:bg-gray-100 rounded-md mb-2 transition duration-300"
                    >
                      <GoPerson size={20} />
                      <span>Dashboard</span>
                    </Link>
                    <Link
                      to="/user/orders"
                      className="flex items-center space-x-2 py-2 text-sm text-black hover:bg-gray-100 rounded-md mb-2 transition duration-300"
                    >
                      <IoBagHandleOutline size={20} className="hover-yellow" />
                      <span>My Orders</span>
                    </Link>
                  </div>
                ) : (
                  <div>
                    <Link
                      to="/signin"
                      className="flex items-center space-x-2 py-2 text-sm text-black hover:bg-yellow-100 rounded-md mb-2 transition duration-300"
                    >
                      <IoBagHandleOutline size={20} className="hover-yellow" />
                      <span>Sign Up/Sign In</span>
                    </Link>
                    <a
                      href="https://marketing.thewinnerindia.com/"
                      className="flex items-center space-x-2 py-2 text-sm text-black hover:bg-yellow-100 rounded-md mb-2 transition duration-300"
                    >
                      <FaBusinessTime size={20} className="hover-yellow" />
                      <span>Business Users Login</span>
                    </a>
                  </div>
                )}

                {user && (
                  <button
                    onClick={handleLogout}
                    className="flex justify-center items-center space-x-2 w-full text-white py-2 rounded-md bg-red-600 hover:bg-red-700 transition duration-300"
                  >
                    <IoIosLogOut size={20} className="hover-yellow" />
                    <span>Logout</span>
                  </button>
                )}
              </div>
            )}
          </div>

          <Link to="/Store/cart" className="relative">
            <BsBag size={25} className="hover-yellow" />
            {cartItemCount > 0 && (
              <div className="absolute z-50 -top-2 -right-2 flex items-center justify-center rounded-full bg-red-500 text-white text-xs w-5 h-5">
                {cartItemCount}
              </div>
            )}
          </Link>

          <Link to="/contactus">
            <GoQuestion size={25} className="hover-yellow" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Navbar;